import { Button } from 'features/components/Button';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LanguageType } from 'types/enums';
import { createSubmitEvent, EntryForm, NextAction } from '../../EntryForm';
import { ErrorModal } from '../../ErrorModal';
import { Entry } from '../../types';
import { useSurveyContext } from '../components/SurveyContext';
import { useCreateSurveyResponseMutation } from '../data/useCreateSurveyResponseMutation';
import { useSurveyForm } from '../data/useSurveyForm';

export const SurveyFormPage = () => {
	const { language, surveyId, pinCode } = useSurveyContext();
	const dictionary = DICTIONARY[language];
	const navigate = useNavigate();

	// Ref to bypass diff modal for surveys since there's no previous data to compare against
	const allowLeavePageOverrideRef = useRef(true);

	const [surveyResponseIsSubmitting, setSurveyResponseIsSubmitting] = useState(false);

	const insertSurveyResponseMutation = useCreateSurveyResponseMutation();

	const surveyDataQuery = useSurveyForm({
		surveyId,
		pinCode: pinCode || undefined
	});

	const submitEntry = async (args: { entry: Entry; action?: NextAction }) => {
		try {
			setSurveyResponseIsSubmitting(true);

			if (!surveyDataQuery.data || !pinCode) {
				console.error(
					"onSubmit called but surveyDataQuery.data or pinCode doesn't exist, this is a no-op"
				);
				return;
			}

			await insertSurveyResponseMutation.mutateAsync({
				surveyId,
				entry: args.entry,
				pinCode: pinCode
			});

			navigate(ROUTE_MAP.surveys.byId.success.createPath({ surveyId, language }));
		} catch (error) {
			// Error is handled in the ErrorModal
			console.error('Survey submission error:', error);
		} finally {
			setSurveyResponseIsSubmitting(false);
		}
	};

	const onSurveySubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
		const form = document.querySelector('form[data-testid="entry-form"]');

		if (form) {
			const submitEvent = createSubmitEvent(e.currentTarget);
			form.dispatchEvent(submitEvent);
		}
	};

	// Redirect to pin entry if no pin code
	if (!pinCode) {
		navigate(ROUTE_MAP.surveys.byId.createPath({ surveyId, language }));
		return null;
	}

	return (
		<>
			<div className="flex flex-col p-8 sm:pt-12 md:pt-20 md:w-[756px] mx-auto pb-20">
				<ErrorModal
					onClose={insertSurveyResponseMutation.reset}
					error={insertSurveyResponseMutation.error}
				/>

				{surveyDataQuery.data && (
					<EntryForm
						projectData={surveyDataQuery.data}
						allowLeavePageOverrideRef={allowLeavePageOverrideRef}
						onSubmitForConfirmation={async ({ nextAction, dirtyData }) => {
							await submitEntry({
								entry: dirtyData,
								action: nextAction
							});
						}}
						onSubmitForSubmission={async ({ nextAction, dirtyData }) => {
							await submitEntry({
								entry: dirtyData,
								action: nextAction
							});
						}}
						submitting={surveyResponseIsSubmitting}
					/>
				)}

				<Button
					loading={surveyResponseIsSubmitting}
					type="submit"
					data-action="save"
					text={dictionary.submitSurvey}
					onClick={onSurveySubmit}
					variant="primary"
					disabled={!surveyDataQuery.data || surveyResponseIsSubmitting}
				/>
			</div>
		</>
	);
};

const DICTIONARY = {
	[LanguageType.English]: {
		submitSurvey: 'Submit survey'
	},
	[LanguageType.Norwegian]: {
		submitSurvey: 'Send inn undersøkelse'
	}
};
